import type { CreateQueryClientOptions } from '@pa/api'
import { AuthStore, createStaffSession, type AuthClient } from '@pa/staff'
import type { RouterHistory } from '@tanstack/react-router'
import { endStartedImpersonation } from '@/features/impersonation'
import { createStaffQueryClient, setupApi } from './api'
import { App } from './App'
import type { StaffEnv } from './env'
import { createAppRouter } from './router'

/**
 * Builds the admin app once: auth store, query client, session, API config and router (boot, tests and stories use
 * the same wiring).
 */
export function createAdminApp(options: {
  env: StaffEnv
  authClient: AuthClient
  history?: RouterHistory
  query?: CreateQueryClientOptions
  /** S-06's hand-off to the clinic app: `window.location.assign` unless a test passes a spy. */
  assign?: (href: string) => void
}) {
  const { env, authClient, history } = options
  const queryClient = createStaffQueryClient(options.query)
  const auth = new AuthStore(authClient)
  const built: { router?: ReturnType<typeof createAppRouter> } = {}
  const session = createStaffSession({
    auth,
    queryClient,
    getRouter: () => {
      if (!built.router) throw new Error('the router is not built yet')
      return built.router
    },
    // Admin sign-out while an impersonation this tab started may be open: #58 `?reason=sign_out` (T-A05-02).
    beforeSignOut: endStartedImpersonation,
  })
  setupApi(env, auth, session)
  const router = createAppRouter({ queryClient, env, auth }, history)
  built.router = router
  const assign =
    options.assign ??
    ((href: string) => {
      window.location.assign(href)
    })
  const element = (
    <App
      queryClient={queryClient}
      router={router}
      auth={auth}
      session={session}
      assign={assign}
      clinicOrigin={env.VITE_CLINIC_APP_ORIGIN}
    />
  )
  return { queryClient, router, auth, session, element }
}
